import type { ShiftTableRow } from './lib/types';
import type { DerivedRosterShift, DerivedTeamRoster } from '../../hooks/types';

type TeamAndRoleData = {
  readonly teamName: string;
  readonly teamType: string;
  readonly teamIon: string;
  readonly roleName: string;
  readonly roleGroup: string;
};

/**
 * Create shift table rows.
 * @param shifts - shifts to create table rows for
 * @param userData - user data to include in the table row
 * @returns shift table rows
 */
const createShiftTableRows =
  (shifts: readonly DerivedRosterShift[], teamAndRoleData: TeamAndRoleData) =>
  (userData?: {
    readonly username: string;
    readonly userFirstName: string;
    readonly userLastName: string;
    readonly userEmploymentType?: string;
  }): readonly ShiftTableRow[] =>
    shifts.map((shift) => ({
      ...teamAndRoleData,
      username: userData?.username ?? '',
      userFirstName: userData?.userFirstName ?? '',
      userLastName: userData?.userLastName ?? '',
      userEmploymentType: userData?.userEmploymentType ?? '',
      key: shift.id,
      shiftStatus: shift.status,
      shiftDateCreated: new Date(shift.createdAt),
      shiftStartTime: new Date(shift.startTime),
      shiftEndTime: new Date(shift.endTime),
    }));

/**
 * Converts a roster to shift table rows.
 * @param teamRosters - team roster to convert to shift table rows
 * @param filter - filter specific shift table rows
 * @returns shift table rows
 */
export const rostersToShiftTableRows = (
  teamRosters: readonly DerivedTeamRoster[],
  filter: 'only vacant shifts' | 'only non-vacant shifts' | 'all shifts'
): readonly ShiftTableRow[] =>
  teamRosters.flatMap((teamRoster) =>
    teamRoster.roleRosters
      .flatMap((role) => {
        const teamAndRoleData: TeamAndRoleData = {
          teamName: teamRoster.teamName,
          teamType: teamRoster.teamType,
          teamIon: teamRoster.internalOrderNumber ?? '',
          roleName: role.roleName,
          roleGroup: role.roleGroup,
        };

        const createRowsForVacantShiftsFunc = createShiftTableRows(
          role.vacantShifts,
          teamAndRoleData
        );

        if (filter === 'only vacant shifts') {
          return createRowsForVacantShiftsFunc();
        }

        const nonVacantShiftRows: readonly ShiftTableRow[] = role.users.flatMap(
          (user) => {
            const username = user.username;
            const userEmail = user.email;
            // We show user email in bracket if email is not same as username.
            const usernameWithEmail =
              username === userEmail
                ? username
                : `${username} (${userEmail ?? 'no email'})`;

            return createShiftTableRows(
              user.shifts,
              teamAndRoleData
            )({
              username: usernameWithEmail,
              userFirstName: user.firstName,
              userLastName: user.lastName,
              userEmploymentType: user.userType,
            });
          }
        );

        if (filter === 'only non-vacant shifts') {
          return nonVacantShiftRows;
        }

        return [...createRowsForVacantShiftsFunc(), ...nonVacantShiftRows];
      })
      // Sorts shifts within teams by their start time.
      .toSorted(
        (a, b) => a.shiftStartTime.getTime() - b.shiftStartTime.getTime()
      )
  );
